import { BoatPreset, scaleAt } from "./BoatTree";
import { BOAT } from "./Config";

/**
 * How big a hull is. Shared because both ends need it: the client to draw a boat, the server
 * to decide whether a cannonball passed through one.
 *
 * These used to live in `src/scripts/boats/Boat.ts` as one client-side constant, which was
 * fine while the arena had nothing to hit. A hit radius has to be the *same* number on both
 * sides — a server that thinks hulls are wider than the client draws them produces shots
 * that visibly miss and score anyway — so it moved here rather than being typed twice.
 *
 * Since ships grow (design/Evolution.md) a size is no longer one constant: it is a function of
 * the **preset** a captain bought and the **scale** their level has grown them to. The
 * module-level constants below are still here, and they are still the starter preset at the
 * bottom of the scale curve — the scale the arena is tuned at — but nothing that draws or
 * shoots at a *particular* ship may use them any more. It uses `fitout.size`.
 *
 * See design/Ships.md. Everything is in world pixels, which are screen pixels: there is no
 * zoom.
 */

/** What a hull measures when the tree has no hulls in it at all. The shipped level 1's length. */
const FALLBACK_LENGTH = 190;

/**
 * Beam as a fraction of length, per hull frame, measured off the **untrimmed** frames in the
 * `boats` sheet — a hull is scaled by its untrimmed height, so its rails sit at half its
 * untrimmed width.
 *
 * It is a table rather than something read off the texture because `shared/` has to compile
 * for Node, where there is no sheet to measure, **and** because the server needs the same box
 * the client draws. The dashboard's preview computes the identical number live from the frame
 * it loaded (`editor/components/BoatPreview.tsx`), which is what makes a slot placed in the
 * panel land in the same place in the arena.
 *
 * **A hull frame the table does not name falls back to level 1's proportions**, and its guns
 * drift off the rails by however much the two frames differ. Pointing a hull level at art that
 * is not in here means adding a row — the same warning this file has always carried about hull
 * level 1, now that every hull has the problem. The rows below are every `boats/hulls/*` frame
 * in the sheet, so a hull picked in the dashboard is covered without touching this file.
 */
const HULL_BEAM_RATIOS: Readonly<Record<string, number>> = {
    "boats/hulls/hull_01": 326 / 320,
    "boats/hulls/hull_02": 344 / 305,
    "boats/hulls/hull_03": 344 / 228,
    "boats/hulls/hull_04": 344 / 324,
    "boats/hulls/hull_05": 305 / 583,
    "boats/hulls/hull_07": 298 / 462,
    "boats/hulls/hull_08": 302 / 459,
    "boats/hulls/hull_09": 302 / 459,
    "boats/hulls/repeating_hull": 305 / 210
};

/**
 * Beam of a hull whose frame is not in the table: `boats/hulls/hull_08`, the frame the shipped
 * tree starts on and the proportions the whole arena was tuned against.
 */
const FALLBACK_BEAM_RATIO = 302 / 459;

/**
 * How close a shot has to pass to a hull's centre to count as a hit, as a fraction of that
 * hull's length.
 *
 * A circle, not a rectangle, and deliberately the same 0.4 the loot pickup uses — they are the
 * same circle now (`pickupRange` in [Battle.ts](Battle.ts)). A hull is
 * about 190 x 125, so a circle is a fair approximation of it from any bearing, and the ship
 * that had two seconds to turn out of the way should not also be punished for presenting its
 * beam. Tighter than this and hits stop feeling earned; wider and a near miss steals 40% of a
 * score.
 *
 * It is a *fraction* rather than a number because hulls now differ: a Galleon is 345px long
 * against a Sloop's 190, and it is hit from proportionally further out. That is the whole
 * bargain design/Pitch.md makes about growing — a bigger ship is a better target — and it is
 * the only cost the hull card actually charges besides handling.
 */
const HIT_RADIUS_FACTOR = 0.4;

/** Every measurement of one hull that anything outside this file needs. All world pixels. */
export interface HullSize {
    /** Bow to stern. */
    length: number;
    /** Rail to rail. */
    beam: number;
    /**
     * Half the box a fitting's hull-local coordinates are fractions of: `x` runs across the
     * beam, `y` along the keel. Same convention the dashboard places slots in — see `Slot` in
     * [BoatTree.ts](BoatTree.ts) and design/Settings.md.
     */
    halfBeam: number;
    halfLength: number;
    /** How close a ball has to pass to count as a hit. See `HIT_RADIUS_FACTOR`. */
    hitRadius: number;
}

/**
 * How big one boat is: its preset's authored length times the `scale` its level has grown it to.
 *
 * **The scale multiplies everything the function returns**, hit radius included, which is the
 * whole cost side of growing (design/Evolution.md): a captain who has levelled is drawn bigger
 * *and* is hit from further out, off one number, and neither half can be taken without the
 * other. It does not touch how the boat handles — speed and turn are the preset's and are fixed
 * for a life — and it does not touch how far its guns reach, which is the gun's own curve.
 *
 * `scale` defaults to 1 for the callers that have no ship in mind: the beam table, a preview, a
 * card in the shop drawing a boat as it would spawn.
 */
export function hullSize(preset: BoatPreset | null | undefined, scale = 1): HullSize {
    const grown = Number.isFinite(scale) && scale > 0 ? scale : 1;
    const length = (preset?.length ?? FALLBACK_LENGTH) * grown;
    const ratio = (preset && HULL_BEAM_RATIOS[preset.frame]) ?? FALLBACK_BEAM_RATIO;
    const beam = length * ratio;

    return { length, beam, halfBeam: beam / 2, halfLength: length / 2, hitRadius: length * HIT_RADIUS_FACTOR };
}

/**
 * Hull level 1's size: the scale reference for the whole arena — cannon range, wake size, loot
 * pickup range and island footprints are all read against it.
 *
 * It is read straight out of `config/boat.json`: everybody in the arena *spawns* on this hull,
 * dashboard makes every distance measured in hull lengths move with it, which is what makes
 * that dial worth having.
 *
 * **It is a convention now rather than a fact.** It used to be "the hull everybody spawns on",
 * which was true while there was one; captains buy different boats and grow them to different
 * sizes, so nobody shares a hull any more and the unit is simply *declared* to be the free boat
 * at the bottom of the scale curve. That is still the right choice — it is the one boat every
 * player has — but it is a decision rather than an observation, and a catalogue that reordered
 * its presets would move every distance in the arena measured in hull lengths.
 *
 * **This is not the size of any particular ship.** Anything drawing or shooting at one reads
 * `fitout.size`, which is its own preset at its own scale. See design/Evolution.md.
 */
export const STARTING_HULL_SIZE = hullSize(BOAT.presets[0]);

/** On-screen length of the free boat at spawn. The unit the arena is tuned in. */
export const BOAT_LENGTH = STARTING_HULL_SIZE.length;
/** On-screen width of that same boat, rail to rail. */
export const BOAT_BEAM = STARTING_HULL_SIZE.beam;

/**
 * The longest any ship in the arena can be: the longest preset in the catalogue, **at the top of
 * the scale curve** — and the widest hit circle that follows from it.
 *
 * What a broad-phase query has to reach by, since the ship a shot is about to find may be the
 * biggest one in the sea — searching by the *average* hull is how a fully grown brig sitting a
 * cell away is missed. The scale is the easy half to forget: a query sized to the authored
 * lengths alone is correct for every fresh captain and silently wrong for every grown one, and
 * a two-boat test would never show it. See `_sweep` in `server/rooms/BattleRoom.ts`.
 *
 * The **length** is exported alongside because the loot walk needs the same bound for the same
 * reason: a pickup circle is a hit circle (design/Battle.md), so one number bounds both searches.
 */
export const MAX_HULL_LENGTH = Math.max(
    STARTING_HULL_SIZE.length,
    ...BOAT.presets.map((preset) => hullSize(preset, scaleAt(BOAT.scale, BOAT.scale.levels)).length)
);

export const MAX_HIT_RADIUS = MAX_HULL_LENGTH * HIT_RADIUS_FACTOR;

/** A thing with a place and a facing. Both `Ship` and the client's predicted boat fit. */
export interface Hull {
    x: number;
    y: number;
    heading: number;
}

/**
 * Where a hull-local point sits in the world, given the hull it is bolted to and how big that
 * hull is.
 *
 * `localX` / `localY` are the fractions a `Slot` stores — -1 the port rail, +1 starboard;
 * -1 the bow, +1 the stern — so this is the one place the deck plan becomes world pixels.
 * Heading 0 is north and grows clockwise, which is the rotation Pixi applies to the hull
 * container, so a gun drawn at a slot and a ball spawned at one come out in the same place.
 *
 * `size` is the ship's **own** hull's, not level 1's: two ships in one arena are different
 * lengths, and a slot is a fraction of whichever one it is bolted to.
 *
 * **Not wrapped.** The caller decides whether the result is a world position (`wrapCoord`
 * it) or an offset. See design/Networking.md on the torus.
 */
export function mountPoint(hull: Hull, localX: number, localY: number, size: HullSize): { x: number; y: number } {
    const across = localX * size.halfBeam;
    const along = localY * size.halfLength;
    const sin = Math.sin(hull.heading);
    const cos = Math.cos(hull.heading);

    return {
        x: hull.x + across * cos - along * sin,
        y: hull.y + across * sin + along * cos
    };
}
